import * as pdfjsLib from 'pdfjs-dist';

// Configure worker for pdfjs-dist - use unpkg CDN to ensure version match
// pdfjsLib.version contains the exact version of the installed package
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/**
 * Converts the first page of a PDF file to an image (Blob)
 * @param {File} pdfFile - The PDF file to convert
 * @returns {Promise<File>} A Promise that resolves to a File containing the image
 */
export async function pdfToImage(pdfFile) {
  try {
    // Read the PDF file as ArrayBuffer
    const arrayBuffer = await pdfFile.arrayBuffer();
    
    // Load the PDF document
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    // Get the first page
    const page = await pdf.getPage(1);
    
    // Set scale for rendering (higher = better quality, but larger file)
    const scale = 2.0;
    const viewport = page.getViewport({ scale });
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    
    // Render the PDF page to the canvas
    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };
    
    await page.render(renderContext).promise;
    
    // Convert canvas to blob (image/jpeg)
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Create a new File object with the same name but .jpg extension
            const imageFile = new File(
              [blob],
              pdfFile.name.replace(/\.pdf$/i, '.jpg'),
              { type: 'image/jpeg' }
            );
            
            // Display file size information
            const originalSizeMB = (pdfFile.size / 1024 / 1024).toFixed(2);
            const convertedSizeMB = (imageFile.size / 1024 / 1024).toFixed(2);
            const originalSizeKB = (pdfFile.size / 1024).toFixed(2);
            const convertedSizeKB = (imageFile.size / 1024).toFixed(2);
            
            console.log(`📊 [PDF Conversion] File size:`, {
              original: `${originalSizeMB} MB (${originalSizeKB} KB)`,
              converted: `${convertedSizeMB} MB (${convertedSizeKB} KB)`,
              reduction: `${((1 - imageFile.size / pdfFile.size) * 100).toFixed(1)}%`
            });
            
            resolve(imageFile);
          } else {
            reject(new Error('Failed to convert canvas to blob'));
          }
        },
        'image/jpeg',
        0.95 // JPEG quality (0.95 = 95%)
      );
    });
  } catch (error) {
    console.error('Error converting PDF to image:', error);
    throw error;
  }
}

/**
 * Checks if a file is a PDF
 * @param {File} file - The file to check
 * @returns {boolean} True if the file is a PDF
 */
export function isPdfFile(file) {
  return file && file.type === 'application/pdf';
}

/**
 * Converts a file to an image if it's a PDF, otherwise returns the original file
 * @param {File} file - The file to convert (PDF or image)
 * @returns {Promise<File>} The converted image file or the original file
 */
export async function convertPdfToImageIfNeeded(file) {
  if (!file) {
    return file;
  }
  
  if (isPdfFile(file)) {
    console.log(`📄 Converting PDF to image: ${file.name}`);
    return await pdfToImage(file);
  }
  return file;
}
